import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  LuShieldCheck,
  LuPlus,
  LuTrash2,
  LuPencil,
  LuCheck,
  LuX,
  LuCircleAlert,
  LuInfo,
  LuFileText,
  LuBuilding2,
  LuUserCheck,
  LuEye,
  LuEyeOff,
  LuLayers,
  LuHandshake,
  LuClipboardList,
  LuUserPlus,
  LuBriefcase,
  LuReceipt,
  LuRotateCcw
} from 'react-icons/lu';
import BASE_URL from '../../url';
import '../CrmStyles.css';

const CANONICAL_STEP_ORDER = [
  'MSA_CLIENT',
  'MSA_SUPPLIER',
  'SUPPLIER_DOC',
  'CAND_COMP_DOC',
  'BGC_CLIENT',
  'BGC_END_CLIENT',
  'REPORTING_DTLS',
  'CLIENT_ID_ASSET',
  'CREATE_LINK_EMP',
  'CREATE_DEPLOYMENT',
  'JOIN_CONFIRM',
  'CLIENT_PO',
  'SUPPLIER_PO'
];

const STEP_LABELS = {
  MSA_CLIENT: {
    title: 'MSA Client',
    badge: 'Client Agreement',
    desc: 'Master Services Agreement executed directly with the client.',
    icon: LuFileText,
    color: '#0284c7'
  },
  MSA_SUPPLIER: {
    title: 'MSA Supplier',
    badge: 'Vendor Agreement',
    desc: 'Master Services Agreement executed with the vendor/supplier.',
    icon: LuHandshake,
    color: '#0ea5e9'
  },
  SUPPLIER_DOC: {
    title: 'Supplier Docu',
    badge: 'Vendor Compliance',
    desc: 'Subcontractor / Vendor documents required by this client for C2C placements (e.g. COI, W9, good standing).',
    icon: LuBuilding2,
    color: '#3b82f6'
  },
  CAND_COMP_DOC: {
    title: 'Candidate Comp. Docu',
    badge: 'Candidate Compliance',
    desc: 'Candidate documents required by this client before starting the assignment (e.g. IDs, certifications, client-specific NDAs).',
    icon: LuUserCheck,
    color: '#3b5bfd'
  },
  BGC_CLIENT: {
    title: 'Client Background Check (BGC)',
    badge: 'Background Screening',
    desc: 'Client-mandated background check and drug screen verifications.',
    icon: LuShieldCheck,
    color: '#8b5cf6'
  },
  BGC_END_CLIENT: {
    title: 'End Client Background Check (BGC)',
    badge: 'End Client Screening',
    desc: 'End client specific background investigations, fingerprinting, and security clearances.',
    icon: LuShieldCheck,
    color: '#6366f1'
  },
  REPORTING_DTLS: {
    title: 'Obtain Reporting Details',
    badge: 'Reporting & Logistics',
    desc: 'Work location, reporting manager contact, day 1 schedule, and orientation details.',
    icon: LuClipboardList,
    color: '#60a5fa'
  },
  CLIENT_ID_ASSET: {
    title: 'Client ID & Asset Issuance',
    badge: 'Asset & Badging',
    desc: 'Client equipment, laptops, security badges, and domain accounts.',
    icon: LuLayers,
    color: '#f59e0b'
  },
  CREATE_LINK_EMP: {
    title: 'Create / Link Employee',
    badge: 'HR Employee',
    desc: 'Create or link the placement to an internal or deployed employee record.',
    icon: LuUserPlus,
    color: '#3b5bfd'
  },
  CREATE_DEPLOYMENT: {
    title: 'Create Deployment',
    badge: 'Deployment',
    desc: 'Generate assignment deployment record with approved billing and pay rates.',
    icon: LuBriefcase,
    color: '#8b5cf6'
  },
  JOIN_CONFIRM: {
    title: 'Joining Confirmation',
    badge: 'Day 1 Confirmation',
    desc: 'Confirm candidate attendance, first-day clock-in, and onboarding completion.',
    icon: LuCheck,
    color: '#2563eb'
  },
  CLIENT_PO: {
    title: 'Client PO',
    badge: 'Client Purchase Order',
    desc: 'Client purchase order issuance, budget authorization, and billing reference.',
    icon: LuReceipt,
    color: '#f97316'
  },
  SUPPLIER_PO: {
    title: 'Supplier PO',
    badge: 'Supplier Purchase Order',
    desc: 'Supplier purchase order and subcontractor payment authorization.',
    icon: LuFileText,
    color: '#ec4899'
  }
};

const CONDITION_OPTIONS = [
  { value: 'ALWAYS', label: 'All Placements (ALWAYS)', badgeClass: 'crm-badge-active', hint: 'Applies to every placement under this client' },
  { value: 'EMP_TYPE_W2', label: 'W2 Candidates Only (EMP_TYPE_W2)', badgeClass: 'crm-badge-role', hint: 'Applies only when candidate is directly hired on W2' },
  { value: 'EMP_TYPE_C2C', label: 'C2C Candidates Only (EMP_TYPE_C2C)', badgeClass: 'crm-badge-pending', hint: 'Applies only when candidate is through a 3rd party supplier' }
];

const CompanyComplianceConfig = ({ companyId, company }) => {
  const [loading, setLoading] = useState(true);
  const [defaults, setDefaults] = useState([]);
  const [clientRequirements, setClientRequirements] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [activeStepTab, setActiveStepTab] = useState('CAND_COMP_DOC');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Modal State for adding/editing client requirement
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('ADD'); // 'ADD' | 'EDIT'
  const [editingReqId, setEditingReqId] = useState(null);
  const [formData, setFormData] = useState({
    stepCode: 'CAND_COMP_DOC',
    itemName: '',
    conditionCode: 'ALWAYS',
    isOptional: false,
    allowMultipleAttachments: false,
    itemSequence: 1,
    notes: ''
  });

  // Custom added steps and edited step labels (persisted per companyId)
  const [customSteps, setCustomSteps] = useState(() => {
    try {
      const saved = localStorage.getItem(`crm_comp_custom_steps_${companyId}`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [stepLabelOverrides, setStepLabelOverrides] = useState(() => {
    try {
      const saved = localStorage.getItem(`crm_comp_step_labels_${companyId}`);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Steps explicitly deleted for this client
  const [deletedStepCodes, setDeletedStepCodes] = useState(() => {
    try {
      const saved = localStorage.getItem(`crm_comp_deleted_steps_${companyId}`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Re-sync states whenever companyId changes
  useEffect(() => {
    try {
      const savedCustom = localStorage.getItem(`crm_comp_custom_steps_${companyId}`);
      setCustomSteps(savedCustom ? JSON.parse(savedCustom) : []);
    } catch {
      setCustomSteps([]);
    }
    try {
      const savedLabels = localStorage.getItem(`crm_comp_step_labels_${companyId}`);
      setStepLabelOverrides(savedLabels ? JSON.parse(savedLabels) : {});
    } catch {
      setStepLabelOverrides({});
    }
    try {
      const savedDeleted = localStorage.getItem(`crm_comp_deleted_steps_${companyId}`);
      setDeletedStepCodes(savedDeleted ? JSON.parse(savedDeleted) : []);
    } catch {
      setDeletedStepCodes([]);
    }
  }, [companyId]);

  // Add Step Modal State
  const [showAddStepModal, setShowAddStepModal] = useState(false);
  const [addStepInsertIndex, setAddStepInsertIndex] = useState(1);
  const [newStepFormData, setNewStepFormData] = useState({
    title: '',
    badge: 'Client Compliance',
    desc: ''
  });

  // Edit Step Modal State
  const [showEditStepModal, setShowEditStepModal] = useState(false);
  const [editingStepCode, setEditingStepCode] = useState(null);
  const [editStepFormData, setEditStepFormData] = useState({
    title: '',
    badge: '',
    desc: ''
  });

  // Workstation Full Checklist Preview Modal State
  const [showWorkstationFullPreview, setShowWorkstationFullPreview] = useState(false);

  const getAuthHeader = () => {
    let token = localStorage.getItem('token');
    if (token && token.startsWith('Bearer ')) token = token.replace('Bearer ', '');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${BASE_URL}/api/crm/companies/${companyId}/compliance-config`, {
        headers: getAuthHeader()
      });

      if (res.data.success) {
        setDefaults(res.data.data.defaults || []);
        setClientRequirements(res.data.data.clientRequirements || []);
        setOverrides(res.data.data.overrides || []);
      } else {
        setError(res.data.message || 'Failed to load compliance config');
      }
    } catch (err) {
      console.error('Error fetching compliance config:', err);
      setError(err.response?.data?.message || 'Failed to load compliance configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchConfig();
    }
  }, [companyId]);

  const showNotification = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  // Check if a default template is excluded for this client
  const isDefaultExcluded = (stepCode, itemName) => {
    const ov = overrides.find(
      (o) => o.StepCode === stepCode && o.ItemName.toLowerCase() === itemName.toLowerCase()
    );
    return ov ? !!ov.IsExcluded : false;
  };

  // Toggle exclusion of a system default template
  const handleToggleDefault = async (stepCode, itemName, currentlyExcluded) => {
    try {
      setActionLoading(true);
      setError('');
      const newExcluded = !currentlyExcluded;

      const res = await axios.post(
        `${BASE_URL}/api/crm/companies/${companyId}/compliance-config/toggle-default`,
        {
          stepCode,
          itemName,
          isExcluded: newExcluded
        },
        { headers: getAuthHeader() }
      );

      if (res.data.success) {
        showNotification(
          newExcluded
            ? `Excluded "${itemName}" from ${company?.CompanyName || 'client'}'s default subtasks.`
            : `Restored "${itemName}" into ${company?.CompanyName || 'client'}'s active subtasks.`
        );
        fetchConfig();
      }
    } catch (err) {
      console.error('Error toggling default exclusion:', err);
      setError(err.response?.data?.message || 'Failed to toggle subtask status');
    } finally {
      setActionLoading(false);
    }
  };

  // Calculate next sequential display order for a given step
  const getNextSequenceForStep = (stepCode) => {
    const stepDefs = defaults.filter((d) => d.StepCode === stepCode);
    const stepReqs = clientRequirements.filter((r) => r.StepCode === stepCode);
    return stepDefs.length + stepReqs.length + 1;
  };

  // Open modal to add requirement
  const handleOpenAddModal = (stepCode) => {
    const targetStep = stepCode || activeStepTab || 'CAND_COMP_DOC';
    setModalMode('ADD');
    setEditingReqId(null);
    setFormData({
      stepCode: targetStep,
      itemName: '',
      conditionCode: 'ALWAYS',
      isOptional: false,
      allowMultipleAttachments: false,
      itemSequence: getNextSequenceForStep(targetStep),
      notes: ''
    });
    setShowModal(true);
  };

  // Open modal to edit existing client requirement
  const handleOpenEditModal = (req) => {
    setModalMode('EDIT');
    setEditingReqId(req.ComplianceRequirementId);
    setFormData({
      stepCode: req.StepCode,
      itemName: req.ItemName,
      conditionCode: req.ConditionCode || 'ALWAYS',
      isOptional: !!req.IsOptional,
      allowMultipleAttachments: !!req.AllowMultipleAttachments,
      itemSequence: req.ItemSequence || getNextSequenceForStep(req.StepCode),
      notes: req.Notes || ''
    });
    setShowModal(true);
  };

  // Submit add or edit
  const handleSaveRequirement = async (e) => {
    e.preventDefault();
    if (!formData.itemName.trim()) {
      setError('Subtask / Document name is required');
      return;
    }

    try {
      setActionLoading(true);
      setError('');

      if (modalMode === 'ADD') {
        const res = await axios.post(
          `${BASE_URL}/api/crm/companies/${companyId}/compliance-config`,
          formData,
          { headers: getAuthHeader() }
        );
        if (res.data.success) {
          showNotification(`Added client subtask "${formData.itemName.trim()}" successfully.`);
          setShowModal(false);
          fetchConfig();
        }
      } else {
        const res = await axios.put(
          `${BASE_URL}/api/crm/companies/${companyId}/compliance-config/${editingReqId}`,
          formData,
          { headers: getAuthHeader() }
        );
        if (res.data.success) {
          showNotification(`Updated subtask "${formData.itemName.trim()}".`);
          setShowModal(false);
          fetchConfig();
        }
      }
    } catch (err) {
      console.error('Error saving compliance requirement:', err);
      setError(err.response?.data?.message || 'Failed to save compliance requirement');
    } finally {
      setActionLoading(false);
    }
  };

  // Delete client requirement
  const handleDeleteRequirement = async (requirementId, itemName) => {
    if (!window.confirm(`Are you sure you want to remove the requirement "${itemName}" for this client?`)) {
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      const res = await axios.delete(
        `${BASE_URL}/api/crm/companies/${companyId}/compliance-config/${requirementId}`,
        { headers: getAuthHeader() }
      );
      if (res.data.success) {
        showNotification(`Removed requirement "${itemName}".`);
        fetchConfig();
      }
    } catch (err) {
      console.error('Error deleting requirement:', err);
      setError(err.response?.data?.message || 'Failed to remove requirement');
    } finally {
      setActionLoading(false);
    }
  };

  // Subtasks for currently selected step
  const currentStepDefaults = useMemo(() => {
    if (!activeStepTab) return [];
    return defaults.filter((d) => d.StepCode === activeStepTab);
  }, [defaults, activeStepTab]);

  const currentStepClientReqs = useMemo(() => {
    if (!activeStepTab) return [];
    return clientRequirements.filter((r) => r.StepCode === activeStepTab);
  }, [clientRequirements, activeStepTab]);

  const availableStepCodes = useMemo(() => {
    const list = [...CANONICAL_STEP_ORDER];

    // Insert custom steps at their stored position
    (customSteps || []).forEach(cs => {
      if (!list.includes(cs.stepCode)) {
        let targetIdx = -1;
        if (cs.insertBeforeStep && list.includes(cs.insertBeforeStep)) {
          targetIdx = list.indexOf(cs.insertBeforeStep);
        } else if (cs.insertAfterStep && list.includes(cs.insertAfterStep)) {
          targetIdx = list.indexOf(cs.insertAfterStep) + 1;
        } else if (typeof cs.insertIndex === 'number' && cs.insertIndex >= 0 && cs.insertIndex <= list.length) {
          targetIdx = cs.insertIndex;
        } else {
          targetIdx = list.length;
        }
        list.splice(targetIdx, 0, cs.stepCode);
      }
    });

    defaults.forEach((d) => {
      if (!list.includes(d.StepCode)) list.push(d.StepCode);
    });
    clientRequirements.forEach((r) => {
      if (!list.includes(r.StepCode)) list.push(r.StepCode);
    });

    // Exclude any deleted steps
    return list.filter((sc) => !(deletedStepCodes || []).includes(sc));
  }, [defaults, clientRequirements, customSteps, deletedStepCodes]);

  // Keep activeStepTab synchronized to a valid step
  useEffect(() => {
    if (availableStepCodes.length > 0) {
      if (!activeStepTab || !availableStepCodes.includes(activeStepTab)) {
        setActiveStepTab(availableStepCodes[0]);
      }
    } else {
      setActiveStepTab('');
    }
  }, [availableStepCodes, activeStepTab]);

  const getStepMetadata = (stepCode) => {
    if (!stepCode) {
      return {
        title: 'No Step Selected',
        badge: '',
        desc: 'Please select or add an onboarding step.',
        icon: LuLayers,
        color: '#6b7280'
      };
    }
    if (stepLabelOverrides[stepCode]) {
      return {
        ...STEP_LABELS[stepCode],
        ...stepLabelOverrides[stepCode],
        icon: STEP_LABELS[stepCode]?.icon || LuLayers,
        color: STEP_LABELS[stepCode]?.color || '#3b5bfd'
      };
    }
    const customMatch = (customSteps || []).find(c => c.stepCode === stepCode);
    if (customMatch) {
      return {
        title: customMatch.title || stepCode,
        badge: customMatch.badge || 'Client Step',
        desc: customMatch.desc || `Custom compliance step for ${company?.CompanyName || 'this client'}`,
        icon: LuLayers,
        color: '#0284c7'
      };
    }
    return STEP_LABELS[stepCode] || {
      title: stepCode,
      badge: 'Step',
      desc: `Subtasks configured for onboarding step ${stepCode}`,
      icon: LuLayers,
      color: '#6b7280'
    };
  };

  const handleOpenAddStepModal = (insertIndex) => {
    setAddStepInsertIndex(insertIndex);
    setNewStepFormData({
      title: '',
      badge: 'Client Compliance',
      desc: ''
    });
    setShowAddStepModal(true);
  };

  const handleSaveNewStep = (e) => {
    e.preventDefault();
    if (!newStepFormData.title.trim()) {
      setError('Step title is required');
      return;
    }

    const cleanName = newStepFormData.title.trim();
    const generatedCode = 'CUST_' + cleanName.toUpperCase().replace(/[^A-Z0-9]/g, '_').slice(0, 20) + '_' + Date.now().toString().slice(-4);

    const insertAfterStep = addStepInsertIndex > 0 && availableStepCodes[addStepInsertIndex - 1]
      ? availableStepCodes[addStepInsertIndex - 1]
      : null;
    const insertBeforeStep = addStepInsertIndex < availableStepCodes.length
      ? availableStepCodes[addStepInsertIndex]
      : null;

    const newStepObj = {
      stepCode: generatedCode,
      title: cleanName,
      badge: newStepFormData.badge || 'Client Mandate',
      desc: newStepFormData.desc || `Mandated onboarding requirement for ${company?.CompanyName || 'this client'}`,
      insertIndex: addStepInsertIndex,
      insertAfterStep,
      insertBeforeStep
    };

    const updated = [...customSteps, newStepObj];
    setCustomSteps(updated);
    try {
      localStorage.setItem(`crm_comp_custom_steps_${companyId}`, JSON.stringify(updated));
    } catch (err) {
      console.error(err);
    }

    if (deletedStepCodes.includes(generatedCode)) {
      const updatedDeleted = deletedStepCodes.filter(c => c !== generatedCode);
      setDeletedStepCodes(updatedDeleted);
      try {
        localStorage.setItem(`crm_comp_deleted_steps_${companyId}`, JSON.stringify(updatedDeleted));
      } catch (err) {
        console.error(err);
      }
    }

    setActiveStepTab(generatedCode);
    setShowAddStepModal(false);
    showNotification(`New step "${cleanName}" added to compliance workflow.`);
  };

  const handleOpenEditStepModal = (stepCode) => {
    const meta = getStepMetadata(stepCode);
    setEditingStepCode(stepCode);
    setEditStepFormData({
      title: meta.title || stepCode,
      badge: meta.badge || '',
      desc: meta.desc || ''
    });
    setShowEditStepModal(true);
  };

  const handleSaveEditStep = (e) => {
    e.preventDefault();
    if (!editStepFormData.title.trim()) {
      setError('Step title is required');
      return;
    }

    const updatedLabels = {
      ...stepLabelOverrides,
      [editingStepCode]: {
        title: editStepFormData.title.trim(),
        badge: editStepFormData.badge || '',
        desc: editStepFormData.desc || ''
      }
    };

    setStepLabelOverrides(updatedLabels);
    try {
      localStorage.setItem(`crm_comp_step_labels_${companyId}`, JSON.stringify(updatedLabels));
    } catch (err) {
      console.error(err);
    }

    setShowEditStepModal(false);
    showNotification(`Step updated to "${editStepFormData.title.trim()}".`);
  };

  // Delete step (works for all steps - predefined canonical or custom)
  const handleDeleteStep = async (stepCode) => {
    if (!stepCode) return;
    const meta = getStepMetadata(stepCode);
    const counts = getStepCounts(stepCode);
    const hasSubtasks = counts.activeTotal > 0;

    const result = await Swal.fire({
      title: `Delete Step "${meta.title}"?`,
      html: `
        <div style="text-align: left; font-size: 13.5px; color: #374151; line-height: 1.5;">
          <p style="margin-bottom: 8px;">
            Are you sure you want to delete the step <strong>"${meta.title}"</strong> from the compliance workflow for <strong>${company?.DisplayName || company?.CompanyName || 'this client'}</strong>?
          </p>
          ${
            hasSubtasks
              ? `<div style="color: #b91c1c; font-size: 12.5px; background: #fee2e2; border: 1px solid #fecaca; padding: 10px 12px; border-radius: 6px; margin-top: 10px;">
                  ⚠️ <strong>Notice:</strong> This step currently has <strong>${counts.activeTotal} active subtask${counts.activeTotal > 1 ? 's' : ''}</strong> (${counts.clientCount} client-mandated, ${counts.activeTotal - counts.clientCount} active default${counts.activeTotal - counts.clientCount !== 1 ? 's' : ''}). Deleting this step will remove it from this client's onboarding checklist.
                </div>`
              : `<p style="color: #6b7280; font-size: 12.5px; margin-top: 6px;">This will remove the step from this client's active onboarding workflow.</p>`
          }
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, Delete Step',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      focusCancel: true
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(true);

      // 1. If it was in customSteps, remove it from customSteps
      const isCustom = (customSteps || []).some(cs => cs.stepCode === stepCode);
      if (isCustom) {
        const updatedCustom = (customSteps || []).filter(cs => cs.stepCode !== stepCode);
        setCustomSteps(updatedCustom);
        try {
          localStorage.setItem(`crm_comp_custom_steps_${companyId}`, JSON.stringify(updatedCustom));
        } catch (err) {
          console.error('Error saving custom steps:', err);
        }
      }

      // 2. Add to deletedStepCodes and persist
      const updatedDeleted = Array.from(new Set([...(deletedStepCodes || []), stepCode]));
      setDeletedStepCodes(updatedDeleted);
      try {
        localStorage.setItem(`crm_comp_deleted_steps_${companyId}`, JSON.stringify(updatedDeleted));
      } catch (err) {
        console.error('Error saving deleted steps:', err);
      }

      // 3. Remove any label overrides for this step
      if (stepLabelOverrides[stepCode]) {
        const updatedLabels = { ...stepLabelOverrides };
        delete updatedLabels[stepCode];
        setStepLabelOverrides(updatedLabels);
        try {
          localStorage.setItem(`crm_comp_step_labels_${companyId}`, JSON.stringify(updatedLabels));
        } catch (err) {
          console.error('Error saving step label overrides:', err);
        }
      }

      // 4. Remove any client-mandated subtasks for this step from the backend
      const reqsToDelete = clientRequirements.filter(r => r.StepCode === stepCode);
      for (const req of reqsToDelete) {
        try {
          await axios.delete(
            `${BASE_URL}/api/crm/companies/${companyId}/compliance-config/${req.ComplianceRequirementId}`,
            { headers: getAuthHeader() }
          );
        } catch (delErr) {
          console.warn('Could not remove client requirement for deleted step:', delErr);
        }
      }
      if (reqsToDelete.length > 0) {
        fetchConfig();
      }

      // 5. Switch activeStepTab to next available step if currently on the deleted step
      const remainingSteps = availableStepCodes.filter(sc => sc !== stepCode && !updatedDeleted.includes(sc));
      if (activeStepTab === stepCode) {
        if (remainingSteps.length > 0) {
          const currentIndex = availableStepCodes.indexOf(stepCode);
          const nextStep = remainingSteps[currentIndex] || remainingSteps[currentIndex - 1] || remainingSteps[0];
          setActiveStepTab(nextStep);
        } else {
          setActiveStepTab('');
        }
      }

      showNotification(`Step "${meta.title}" deleted from compliance workflow.`);

      Swal.fire({
        icon: 'success',
        title: 'Step Deleted',
        text: `Step "${meta.title}" has been deleted successfully.`,
        timer: 1800,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Error deleting step:', err);
      setError('Failed to delete step. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Restore a specific deleted step
  const handleRestoreDeletedStep = (stepCodeToRestore) => {
    const updated = (deletedStepCodes || []).filter(sc => sc !== stepCodeToRestore);
    setDeletedStepCodes(updated);
    try {
      localStorage.setItem(`crm_comp_deleted_steps_${companyId}`, JSON.stringify(updated));
    } catch (err) {
      console.error('Error saving deleted steps:', err);
    }
    setActiveStepTab(stepCodeToRestore);
    showNotification(`Restored step "${getStepMetadata(stepCodeToRestore).title}".`);
  };

  // Restore all deleted steps
  const handleResetAllDeletedSteps = async () => {
    const result = await Swal.fire({
      title: 'Restore Deleted Steps?',
      text: `Do you want to restore ${deletedStepCodes.length} deleted step(s) back into the compliance workflow?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3b5bfd',
      confirmButtonText: 'Yes, Restore All',
      cancelButtonText: 'Cancel'
    });
    if (!result.isConfirmed) return;

    setDeletedStepCodes([]);
    try {
      localStorage.removeItem(`crm_comp_deleted_steps_${companyId}`);
    } catch (err) {
      console.error(err);
    }
    showNotification('All deleted steps restored successfully.');
  };

  const getStepCounts = (stepCode) => {
    const stepDefs = defaults.filter((d) => d.StepCode === stepCode);
    const activeDefs = stepDefs.filter((d) => !isDefaultExcluded(stepCode, d.ItemName));
    const clientReqs = clientRequirements.filter((r) => r.StepCode === stepCode);
    return {
      activeTotal: activeDefs.length + clientReqs.length,
      defaultCount: stepDefs.length,
      clientCount: clientReqs.length
    };
  };

  if (loading) {
    return (
      <div className="crm-card" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
        <div style={{ display: 'inline-block', width: '28px', height: '28px', border: '3px solid #e5e7eb', borderTopColor: '#3b5bfd', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <p style={{ marginTop: '12px', fontSize: '14px' }}>Loading client compliance configuration...</p>
      </div>
    );
  }

  const stepMeta = getStepMetadata(activeStepTab);
  const StepIcon = stepMeta.icon || LuLayers;

  return (
    <div className="crm-compliance-config-wrapper" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Top Banner */}
      <div
        className="crm-card"
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)',
          borderLeft: '4px solid #3b5bfd',
          padding: '20px 24px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: 600, color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LuShieldCheck size={20} color="#3b5bfd" />
              Client Compliance &amp; Onboarding Requirements
            </h3>
            <p style={{ margin: 0, fontSize: '13.5px', color: '#4b5563', maxWidth: '780px', lineHeight: '1.5' }}>
              Manage subtask requirements mandated by <strong>{company?.DisplayName || company?.CompanyName || 'this client'}</strong>.
              When candidate placements or supplier engagements are onboarded under this client, these configured subtasks
              will automatically seed into the onboarding workflow.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {deletedStepCodes.length > 0 && (
              <button
                type="button"
                className="crm-btn-secondary"
                onClick={handleResetAllDeletedSteps}
                style={{ padding: '8px 14px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#b45309', borderColor: '#fcd34d', background: '#fffbeb' }}
                title="Restore all deleted steps"
              >
                <LuRotateCcw size={14} /> Restore Steps ({deletedStepCodes.length})
              </button>
            )}
            <button
              type="button"
              className="crm-btn-secondary"
              onClick={() => setShowWorkstationFullPreview(true)}
              style={{ padding: '8px 16px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
              title="Preview how this compliance workflow renders in Onboarding Management Workstation"
            >
              <LuEye size={15} /> Preview in Workstation
            </button>
            <button
              type="button"
              className="crm-btn-primary"
              onClick={() => handleOpenAddModal(activeStepTab)}
              style={{ padding: '8px 16px', fontSize: '13px' }}
            >
              <LuPlus size={16} /> Add Compliance Subtask
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div style={{ marginTop: '14px', color: '#ef4444', fontSize: '13.5px', background: '#fef2f2', padding: '10px 14px', borderRadius: '6px', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LuCircleAlert size={16} />
            <span>{error}</span>
          </div>
        )}

        {successMsg && (
          <div style={{ marginTop: '14px', color: '#065f46', fontSize: '13.5px', background: '#ecfdf5', padding: '10px 14px', borderRadius: '6px', border: '1px solid #6ee7b7', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LuCheck size={16} />
            <span>{successMsg}</span>
          </div>
        )}
      </div>

      {/* Step Tabs with In-Between, First & Last (+) Buttons and Right-Side Pen & Trash Icons */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid #e5e7eb', paddingBottom: '10px' }}>
        {/* First (+) button: Insert before the very first step */}
        {availableStepCodes.length > 0 && (
          <button
            type="button"
            className="crm-step-add-between-btn"
            onClick={() => handleOpenAddStepModal(0)}
            title={`Insert new step at the beginning (before "${getStepMetadata(availableStepCodes[0]).title}")`}
          >
            <LuPlus size={13} />
          </button>
        )}

        {availableStepCodes.map((stepCode, idx) => {
          const meta = getStepMetadata(stepCode);
          const counts = getStepCounts(stepCode);
          const isActive = activeStepTab === stepCode;

          return (
            <React.Fragment key={stepCode}>
              {idx > 0 && (
                <button
                  type="button"
                  className="crm-step-add-between-btn"
                  onClick={() => handleOpenAddStepModal(idx)}
                  title={`Insert new step between "${getStepMetadata(availableStepCodes[idx - 1]).title}" and "${meta.title}"`}
                >
                  <LuPlus size={13} />
                </button>
              )}

              <button
                type="button"
                onClick={() => setActiveStepTab(stepCode)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '7px 12px',
                  borderRadius: '8px',
                  border: isActive ? `1.5px solid ${meta.color || '#3b5bfd'}` : '1px solid #e5e7eb',
                  background: isActive ? '#eff6ff' : '#ffffff',
                  color: isActive ? '#3b5bfd' : '#4b5563',
                  fontWeight: isActive ? 600 : 500,
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                <span>{meta.title}</span>
                <span
                  style={{
                    background: isActive ? '#3b5bfd' : '#e5e7eb',
                    color: isActive ? '#ffffff' : '#374151',
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '1px 6px',
                    borderRadius: '12px'
                  }}
                >
                  {counts.activeTotal}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  className="crm-step-edit-pen-btn"
                  title={`Edit step name: "${meta.title}"`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEditStepModal(stepCode);
                  }}
                >
                  <LuPencil size={11} />
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  className="crm-step-delete-btn"
                  title={`Delete step: "${meta.title}"`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteStep(stepCode);
                  }}
                >
                  <LuTrash2 size={11} />
                </span>
              </button>
            </React.Fragment>
          );
        })}

        {/* Last (+) button: Insert after the very last step */}
        {availableStepCodes.length > 0 && (
          <button
            type="button"
            className="crm-step-add-between-btn"
            onClick={() => handleOpenAddStepModal(availableStepCodes.length)}
            title={`Insert new step at the end (after "${getStepMetadata(availableStepCodes[availableStepCodes.length - 1]).title}")`}
          >
            <LuPlus size={13} />
          </button>
        )}

        {availableStepCodes.length === 0 && (
          <div style={{ padding: '14px 10px', color: '#6b7280', fontSize: '13.5px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span>No active steps configured for this client.</span>
            <button
              type="button"
              className="crm-btn-primary"
              onClick={() => handleOpenAddStepModal(0)}
              style={{ padding: '5px 12px', fontSize: '12.5px' }}
            >
              <LuPlus size={13} /> Add Step
            </button>
            {deletedStepCodes.length > 0 && (
              <button
                type="button"
                className="crm-btn-secondary"
                onClick={handleResetAllDeletedSteps}
                style={{ padding: '5px 12px', fontSize: '12.5px' }}
              >
                <LuRotateCcw size={12} /> Restore All Deleted Steps
              </button>
            )}
          </div>
        )}
      </div>

      {/* Step Header Card */}
      <div className="crm-card" style={{ padding: '16px 20px', background: '#ffffff', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '38px', height: '38px', borderRadius: '8px', background: 'rgba(1, 157, 136, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <StepIcon size={20} color={stepMeta.color || '#3b5bfd'} />
          </div>
          <div>
            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
              {stepMeta.title}
            </h4>
            <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#6b7280' }}>
              {stepMeta.desc}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {activeStepTab && (
            <>
              <button
                type="button"
                className="crm-btn-secondary"
                onClick={() => handleOpenEditStepModal(activeStepTab)}
                style={{ padding: '6px 12px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                title="Edit step details"
              >
                <LuPencil size={13} /> Edit Step
              </button>
              <button
                type="button"
                className="crm-btn-secondary"
                onClick={() => handleDeleteStep(activeStepTab)}
                style={{ padding: '6px 12px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#ef4444', borderColor: '#fca5a5' }}
                title="Delete this step"
              >
                <LuTrash2 size={13} /> Delete Step
              </button>
            </>
          )}
          <button
            type="button"
            className="crm-btn-primary"
            onClick={() => handleOpenAddModal(activeStepTab)}
            style={{ padding: '6px 14px', fontSize: '13px' }}
            disabled={!activeStepTab}
          >
            <LuPlus size={15} /> Add Subtask for {stepMeta.title}
          </button>
        </div>
      </div>

      {/* SECTION 1: Client-Specific Mandated Requirements */}
      <div className="crm-card">
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafbfc' }}>
          <div>
            <h5 style={{ margin: 0, fontSize: '14.5px', fontWeight: 600, color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b5bfd' }} />
              Client-Mandated Subtasks ({currentStepClientReqs.length})
            </h5>
            <span style={{ fontSize: '12.5px', color: '#6b7280' }}>
              Custom requirements required exclusively for placements at {company?.CompanyName || 'this client'}
            </span>
          </div>

          {currentStepClientReqs.length > 0 && (
            <span style={{ fontSize: '12px', background: '#ecfdf5', color: '#065f46', padding: '3px 8px', borderRadius: '4px', fontWeight: 500 }}>
              {currentStepClientReqs.length} Active Client Requirement{currentStepClientReqs.length > 1 ? 's' : ''}
            </span>
          )}
        </div>

        {currentStepClientReqs.length === 0 ? (
          <div style={{ padding: '32px 20px', textAlign: 'center', color: '#9ca3af' }}>
            <LuShieldCheck size={32} style={{ opacity: 0.5, marginBottom: '8px' }} />
            <p style={{ margin: 0, fontSize: '13.5px', color: '#6b7280' }}>
              No custom client subtasks added for {stepMeta.title} yet.
            </p>
            <p style={{ margin: '4px 0 14px 0', fontSize: '12px', color: '#9ca3af' }}>
              Standard system defaults will be applied automatically unless customized.
            </p>
            <button
              type="button"
              className="crm-btn-secondary"
              onClick={() => handleOpenAddModal(activeStepTab)}
              style={{ fontSize: '12.5px', padding: '6px 12px' }}
            >
              <LuPlus size={14} /> Add Client-Specific Requirement
            </button>
          </div>
        ) : (
          <div className="crm-table-responsive">
            <table className="crm-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#4b5563', width: '40px' }}>#</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#4b5563' }}>Subtask / Document Name</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#4b5563', width: '150px' }}>Applicability</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#4b5563', width: '100px' }}>Mandatory</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#4b5563' }}>Notes / Instructions</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#4b5563', width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentStepClientReqs.map((req, idx) => {
                  const cond = CONDITION_OPTIONS.find((c) => c.value === req.ConditionCode) || CONDITION_OPTIONS[0];

                  return (
                    <tr key={req.ComplianceRequirementId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>
                        {idx + 1}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: 600, color: '#1f2937', fontSize: '13.5px' }}>
                            {req.ItemName}
                          </span>
                          <span style={{ fontSize: '10.5px', background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>
                            CLIENT
                          </span>
                          {req.AllowMultipleAttachments ? (
                            <span style={{ fontSize: '10px', background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', padding: '1px 5px', borderRadius: '4px', fontWeight: 600 }}>
                              Multi-File
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span className={`crm-badge ${cond.badgeClass}`} style={{ fontSize: '11px' }}>
                          {req.ConditionCode === 'ALWAYS' ? 'All Hires' : req.ConditionCode === 'EMP_TYPE_W2' ? 'W2 Only' : 'C2C Only'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {req.IsOptional ? (
                          <span style={{ fontSize: '11.5px', color: '#6b7280', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>Optional</span>
                        ) : (
                          <span style={{ fontSize: '11.5px', color: '#b91c1c', background: '#fee2e2', padding: '2px 6px', borderRadius: '4px', fontWeight: 500 }}>Required</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '12.5px', color: '#4b5563' }}>
                        {req.Notes ? (
                          <span title={req.Notes} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {req.Notes}
                          </span>
                        ) : (
                          <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No notes</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(req)}
                            disabled={actionLoading}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b5bfd', padding: '4px' }}
                            title="Edit Subtask"
                          >
                            <LuPencil size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteRequirement(req.ComplianceRequirementId, req.ItemName)}
                            disabled={actionLoading}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px' }}
                            title="Delete Requirement"
                          >
                            <LuTrash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* SECTION 2: Standard System Default Templates */}
      <div className="crm-card">
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafbfc' }}>
          <div>
            <h5 style={{ margin: 0, fontSize: '14.5px', fontWeight: 600, color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#6b7280' }} />
              System Default Subtasks ({currentStepDefaults.length})
            </h5>
            <span style={{ fontSize: '12.5px', color: '#6b7280' }}>
              Standard templates defined in the system catalog. You can exclude individual items if this client does not require them.
            </span>
          </div>

          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {currentStepDefaults.filter((d) => !isDefaultExcluded(activeStepTab, d.ItemName)).length} of {currentStepDefaults.length} active
          </div>
        </div>

        {currentStepDefaults.length === 0 ? (
          <div style={{ padding: '24px 20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
            No default templates defined for {stepMeta.title}.
          </div>
        ) : (
          <div className="crm-table-responsive">
            <table className="crm-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#4b5563', width: '40px' }}>#</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#4b5563' }}>Standard Document / Subtask</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#4b5563', width: '150px' }}>Applicability</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#4b5563', width: '110px' }}>Mandatory</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#4b5563', width: '160px' }}>Status for this Client</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#4b5563', width: '130px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentStepDefaults.map((item, idx) => {
                  const excluded = isDefaultExcluded(activeStepTab, item.ItemName);
                  const isW2 = item.ConditionCode === 'EMP_TYPE_W2';

                  return (
                    <tr
                      key={item.OnboardingStepItemTemplateId}
                      style={{
                        borderBottom: '1px solid #f3f4f6',
                        background: excluded ? '#fafafa' : '#ffffff',
                        opacity: excluded ? 0.6 : 1
                      }}
                    >
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>
                        {idx + 1}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: 500, color: excluded ? '#9ca3af' : '#1f2937', fontSize: '13.5px', textDecoration: excluded ? 'line-through' : 'none' }}>
                            {item.ItemName}
                          </span>
                          <span style={{ fontSize: '10px', background: '#f3f4f6', color: '#6b7280', padding: '1px 5px', borderRadius: '4px', fontWeight: 600 }}>
                            DEFAULT
                          </span>
                          {item.AllowMultipleAttachments ? (
                            <span style={{ fontSize: '10px', background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', padding: '1px 5px', borderRadius: '4px', fontWeight: 600 }}>
                              Multi-File
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span className={`crm-badge ${isW2 ? 'crm-badge-role' : 'crm-badge-active'}`} style={{ fontSize: '11px' }}>
                          {isW2 ? 'W2 Candidates Only' : 'All Hires (ALWAYS)'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {item.IsOptional ? (
                          <span style={{ fontSize: '11.5px', color: '#6b7280', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>Optional</span>
                        ) : (
                          <span style={{ fontSize: '11.5px', color: '#1d4ed8', background: '#eff6ff', padding: '2px 6px', borderRadius: '4px', fontWeight: 500 }}>Required</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {excluded ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#dc2626', fontWeight: 500 }}>
                            <LuEyeOff size={14} /> Excluded from Client
                          </span>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#2563eb', fontWeight: 500 }}>
                            <LuCheck size={14} /> Active for Client
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <button
                          type="button"
                          disabled={actionLoading}
                          onClick={() => handleToggleDefault(activeStepTab, item.ItemName, excluded)}
                          style={{
                            padding: '4px 10px',
                            fontSize: '12px',
                            borderRadius: '5px',
                            border: excluded ? '1px solid #10b981' : '1px solid #f87171',
                            background: excluded ? '#ecfdf5' : '#fff1f2',
                            color: excluded ? '#047857' : '#e11d48',
                            cursor: 'pointer',
                            fontWeight: 500,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          {excluded ? (
                            <>
                              <LuEye size={12} /> Re-Include
                            </>
                          ) : (
                            <>
                              <LuEyeOff size={12} /> Exclude
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODAL: Add / Edit Client Requirement */}
      {showModal && (
        <div
          className="crm-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            className="crm-card"
            style={{
              width: '540px',
              maxWidth: '90vw',
              padding: 0,
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
          >
            {/* Modal Header */}
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafbfc' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(1, 157, 136, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LuShieldCheck size={18} color="#3b5bfd" />
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                    {modalMode === 'ADD' ? 'Add Client Compliance Subtask' : 'Edit Compliance Subtask'}
                  </h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                    Mandatory for placements at {company?.CompanyName || 'Client'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}
              >
                <LuX size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSaveRequirement}>
              <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Step Selector */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                    Onboarding Step Target *
                  </label>
                  <select
                    className="crm-select"
                    value={formData.stepCode}
                    onChange={(e) => {
                      const newStep = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        stepCode: newStep,
                        ...(modalMode === 'ADD' ? { itemSequence: getNextSequenceForStep(newStep) } : {})
                      }));
                    }}
                    required
                  >
                    {availableStepCodes.map((sc) => (
                      <option key={sc} value={sc}>
                        {STEP_LABELS[sc]?.title || sc} ({sc})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Subtask Name */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                    Document / Subtask Name *
                  </label>
                  <input
                    type="text"
                    className="crm-input"
                    placeholder="e.g. Client Specific NDA, Drug Screening 10-Panel, Security Declaration"
                    value={formData.itemName}
                    onChange={(e) => {
                      const val = e.target.value;
                      const lower = val.toLowerCase();
                      const shouldAutoMulti = modalMode === 'ADD' && (lower.includes('bgv') || lower.includes('supporting doc'));
                      setFormData(prev => ({
                        ...prev,
                        itemName: val,
                        ...(shouldAutoMulti ? { allowMultipleAttachments: true } : {})
                      }));
                    }}
                    required
                  />
                </div>

                {/* Applicability / Condition */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                    Candidate Employment Condition *
                  </label>
                  <select
                    className="crm-select"
                    value={formData.conditionCode}
                    onChange={(e) => setFormData({ ...formData, conditionCode: e.target.value })}
                  >
                    {CONDITION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <span style={{ fontSize: '11.5px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                    {CONDITION_OPTIONS.find((c) => c.value === formData.conditionCode)?.hint}
                  </span>
                </div>

                {/* Sequence & Optional Toggle */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                      Display Order (Sequence)
                    </label>
                    <input
                      type="number"
                      min="1"
                      className="crm-input"
                      value={formData.itemSequence}
                      onChange={(e) => setFormData({ ...formData, itemSequence: parseInt(e.target.value, 10) || 1 })}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                      Requirement Type
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', height: '42px' }}>
                      <input
                        type="checkbox"
                        checked={formData.isOptional}
                        onChange={(e) => setFormData({ ...formData, isOptional: e.target.checked })}
                        style={{ width: '16px', height: '16px', accentColor: '#3b5bfd' }}
                      />
                      <span style={{ fontSize: '13px', color: '#4b5563' }}>Optional (Not blocking)</span>
                    </label>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                      Attachment Type
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', height: '42px' }}>
                      <input
                        type="checkbox"
                        checked={!!formData.allowMultipleAttachments}
                        onChange={(e) => setFormData({ ...formData, allowMultipleAttachments: e.target.checked })}
                        style={{ width: '16px', height: '16px', accentColor: '#3b5bfd' }}
                      />
                      <span style={{ fontSize: '13px', color: '#4b5563' }}>Allow Multiple Attachments (e.g. BGV docs)</span>
                    </label>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                    Special Instructions / Notes (Optional)
                  </label>
                  <textarea
                    rows={3}
                    className="crm-input"
                    placeholder="e.g. Upload candidate signed copy of client annexure 4 before day 1."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{ padding: '14px 22px', borderTop: '1px solid #e5e7eb', background: '#fafbfc', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  className="crm-btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={actionLoading}
                  style={{ padding: '6px 14px', fontSize: '13px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="crm-btn-primary"
                  disabled={actionLoading}
                  style={{ padding: '6px 18px', fontSize: '13px' }}
                >
                  {actionLoading ? 'Saving...' : modalMode === 'ADD' ? 'Add Requirement' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* MODAL 2: Add New Compliance Step Modal with Live Workstation Preview */}
      {showAddStepModal && (
        <div className="crm-modal-overlay">
          <div className="crm-modal" style={{ maxWidth: '640px', width: '92%' }}>
            <div className="crm-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600 }}>Add New Compliance Step</h3>
                <span style={{ fontSize: '12.5px', color: '#6b7280' }}>
                  {addStepInsertIndex === 0 ? (
                    <>Inserting at the <strong>beginning</strong> (before <strong>{getStepMetadata(availableStepCodes[0])?.title || 'first step'}</strong>)</>
                  ) : addStepInsertIndex >= availableStepCodes.length ? (
                    <>Inserting at the <strong>end</strong> (after <strong>{getStepMetadata(availableStepCodes[availableStepCodes.length - 1])?.title || 'last step'}</strong>)</>
                  ) : (
                    <>Inserting between <strong>{getStepMetadata(availableStepCodes[addStepInsertIndex - 1])?.title || 'Start'}</strong> and <strong>{getStepMetadata(availableStepCodes[addStepInsertIndex])?.title || 'End'}</strong></>
                  )}
                </span>
              </div>
              <button
                type="button"
                className="crm-modal-close-btn"
                onClick={() => setShowAddStepModal(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280' }}
              >
                <LuX size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveNewStep}>
              <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                    Step Name / Title <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="crm-input"
                    placeholder="e.g. Vendor Clearance & COI, Work Authorization (I-9)"
                    value={newStepFormData.title}
                    onChange={(e) => setNewStepFormData({ ...newStepFormData, title: e.target.value })}
                    required
                    autoFocus
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                      Badge / Category
                    </label>
                    <input
                      type="text"
                      className="crm-input"
                      placeholder="e.g. Client Mandate, Screening"
                      value={newStepFormData.badge}
                      onChange={(e) => setNewStepFormData({ ...newStepFormData, badge: e.target.value })}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                      Position in Workflow
                    </label>
                    <div style={{ fontSize: '13px', padding: '9px 12px', background: '#f1f5f9', borderRadius: '6px', color: '#475569', fontWeight: 500 }}>
                      Step Position #{addStepInsertIndex + 1}
                      {addStepInsertIndex === 0
                        ? ' (First Step)'
                        : addStepInsertIndex >= availableStepCodes.length
                        ? ' (Last Step)'
                        : ` (Between "${getStepMetadata(availableStepCodes[addStepInsertIndex - 1])?.title}" and "${getStepMetadata(availableStepCodes[addStepInsertIndex])?.title}")`}
                    </div>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                    Step Description / Purpose
                  </label>
                  <textarea
                    rows={2}
                    className="crm-input"
                    placeholder="Brief description of requirements under this onboarding step..."
                    value={newStepFormData.desc}
                    onChange={(e) => setNewStepFormData({ ...newStepFormData, desc: e.target.value })}
                  />
                </div>

                {/* Live Workstation Checklist Preview */}
                <div className="crm-workstation-preview-box">
                  <div className="crm-preview-header">
                    <LuEye size={14} color="#0369a1" />
                    <span>How it will appear in Onboarding Management Workstation</span>
                  </div>
                  <div className="crm-preview-row">
                    <div className="crm-preview-left">
                      <span className="crm-preview-seq">{addStepInsertIndex + 1}</span>
                      <div>
                        <span className="crm-preview-title">
                          {newStepFormData.title.trim() || 'New Step Title'} <span style={{ color: '#ef4444' }}>*</span>
                        </span>
                        <span className="crm-preview-badge" style={{ marginLeft: '8px' }}>
                          {newStepFormData.badge || 'Client Mandate'}
                        </span>
                      </div>
                    </div>
                    <div className="crm-preview-right">
                      <span style={{ fontSize: '11px', background: '#f1f5f9', border: '1px solid #e2e8f0', padding: '2px 8px', borderRadius: '12px', color: '#475569' }}>
                        0/0 Subtasks
                      </span>
                      <span className="crm-preview-select">-- Status (Default) --</span>
                      <span className="crm-preview-assignee">Assignee</span>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', background: '#fafbfc', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  className="crm-btn-secondary"
                  onClick={() => setShowAddStepModal(false)}
                  style={{ padding: '6px 14px', fontSize: '13px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="crm-btn-primary"
                  style={{ padding: '6px 18px', fontSize: '13px' }}
                >
                  <LuPlus size={15} /> Add Step to Sequence
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: Edit Compliance Step Modal with Live Workstation Preview */}
      {showEditStepModal && (
        <div className="crm-modal-overlay">
          <div className="crm-modal" style={{ maxWidth: '640px', width: '92%' }}>
            <div className="crm-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600 }}>Edit Compliance Step</h3>
                <span style={{ fontSize: '12.5px', color: '#6b7280' }}>
                  Step Code: <code>{editingStepCode}</code>
                </span>
              </div>
              <button
                type="button"
                className="crm-modal-close-btn"
                onClick={() => setShowEditStepModal(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280' }}
              >
                <LuX size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveEditStep}>
              <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                    Step Name / Title <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="crm-input"
                    value={editStepFormData.title}
                    onChange={(e) => setEditStepFormData({ ...editStepFormData, title: e.target.value })}
                    required
                    autoFocus
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                    Badge / Category
                  </label>
                  <input
                    type="text"
                    className="crm-input"
                    value={editStepFormData.badge}
                    onChange={(e) => setEditStepFormData({ ...editStepFormData, badge: e.target.value })}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                    Step Description
                  </label>
                  <textarea
                    rows={2}
                    className="crm-input"
                    value={editStepFormData.desc}
                    onChange={(e) => setEditStepFormData({ ...editStepFormData, desc: e.target.value })}
                  />
                </div>

                {/* Live Workstation Checklist Preview */}
                <div className="crm-workstation-preview-box">
                  <div className="crm-preview-header">
                    <LuEye size={14} color="#0369a1" />
                    <span>Updated Appearance in Onboarding Workstation</span>
                  </div>
                  <div className="crm-preview-row">
                    <div className="crm-preview-left">
                      <span className="crm-preview-seq">
                        {availableStepCodes.indexOf(editingStepCode) + 1}
                      </span>
                      <div>
                        <span className="crm-preview-title">
                          {editStepFormData.title.trim() || 'Step Name'} <span style={{ color: '#ef4444' }}>*</span>
                        </span>
                        {editStepFormData.badge && (
                          <span className="crm-preview-badge" style={{ marginLeft: '8px' }}>
                            {editStepFormData.badge}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="crm-preview-right">
                      <span style={{ fontSize: '11px', background: '#f1f5f9', border: '1px solid #e2e8f0', padding: '2px 8px', borderRadius: '12px', color: '#475569' }}>
                        {getStepCounts(editingStepCode).activeTotal} Subtasks
                      </span>
                      <span className="crm-preview-select">-- Status (Default) --</span>
                      <span className="crm-preview-assignee">Assignee</span>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', background: '#fafbfc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => {
                    const targetCode = editingStepCode;
                    setShowEditStepModal(false);
                    handleDeleteStep(targetCode);
                  }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12.5px',
                    background: '#fee2e2',
                    color: '#b91c1c',
                    border: '1px solid #fca5a5',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontWeight: 500
                  }}
                  title="Delete this step entirely"
                >
                  <LuTrash2 size={13} /> Delete Step
                </button>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    className="crm-btn-secondary"
                    onClick={() => setShowEditStepModal(false)}
                    style={{ padding: '6px 14px', fontSize: '13px' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="crm-btn-primary"
                    style={{ padding: '6px 18px', fontSize: '13px' }}
                  >
                    <LuCheck size={15} /> Save Step Changes
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 4: Full Workstation Checklist Preview Simulator */}
      {showWorkstationFullPreview && (
        <div className="crm-modal-overlay">
          <div className="crm-modal" style={{ maxWidth: '850px', width: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="crm-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <LuBuilding2 size={18} color="#0369a1" />
                  Onboarding Steps &amp; Compliance ({company?.DisplayName || company?.CompanyName || 'Client'})
                </h3>
                <span style={{ fontSize: '12.5px', color: '#64748b' }}>
                  Workstation Checklist Simulator — Exactly how recruiters will see and manage this client's steps
                </span>
              </div>
              <button
                type="button"
                className="crm-modal-close-btn"
                onClick={() => setShowWorkstationFullPreview(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280' }}
              >
                <LuX size={20} />
              </button>
            </div>

            <div style={{ padding: '20px', overflowY: 'auto', flex: 1, background: '#f8fafc' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                  Total Workflow: {availableStepCodes.length} Sequential Steps
                </span>
                <span style={{ fontSize: '12px', background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', padding: '3px 10px', borderRadius: '12px', fontWeight: 600 }}>
                  0 / {availableStepCodes.length} Completed
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {availableStepCodes.map((sc, idx) => {
                  const meta = getStepMetadata(sc);
                  const counts = getStepCounts(sc);

                  return (
                    <div
                      key={sc}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '11px 16px',
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderLeft: `4px solid ${meta.color || '#3b5bfd'}`,
                        borderRadius: '6px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                        flexWrap: 'wrap',
                        gap: '10px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '240px', flex: 1 }}>
                        <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#eff6ff', color: '#3b5bfd', fontSize: '12px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          {idx + 1}
                        </span>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '14px', fontWeight: 650, color: '#0f172a' }}>
                              {meta.title}
                            </span>
                            <span style={{ color: '#ef4444', fontWeight: 700 }}>*</span>
                            {meta.badge && (
                              <span style={{ fontSize: '10.5px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '1px 6px', borderRadius: '10px' }}>
                                {meta.badge}
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>
                            {counts.activeTotal} mandated subtask{counts.activeTotal !== 1 ? 's' : ''} configured
                          </span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <select
                          disabled
                          style={{
                            padding: '5px 10px',
                            borderRadius: '4px',
                            border: '1px solid #cbd5e1',
                            fontSize: '12px',
                            background: '#f8fafc',
                            color: '#64748b'
                          }}
                        >
                          <option>-- Status (Default) --</option>
                        </select>
                        <input
                          type="text"
                          disabled
                          placeholder="Assignee"
                          style={{
                            width: '110px',
                            padding: '5px 8px',
                            borderRadius: '4px',
                            border: '1px solid #cbd5e1',
                            fontSize: '12px',
                            background: '#ffffff',
                            color: '#94a3b8'
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ padding: '14px 20px', borderTop: '1px solid #e5e7eb', background: '#ffffff', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="crm-btn-primary"
                onClick={() => setShowWorkstationFullPreview(false)}
                style={{ padding: '7px 18px', fontSize: '13px' }}
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyComplianceConfig;
